import { useIntersectionObserver } from "@react-hookz/web";
import type { FunctionComponent } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { config } from "shared/data";

import Phone from "./phone.png";
import {
  Alert,
  AlertContainer,
  BaseLinkButton,
  Button,
  ButtonContainer,
  Container,
  ImageContainer,
  ImagePhone,
  Point,
  PointLink,
  Points,
  SvgAlert,
  SvgAlertOn,
  TextWrapper,
  Title,
  Toggle,
  ToggleCircle,
} from "./styles";

type Props = {
  isShowSetupButton?: boolean;
};

export const SettingUpAlert: FunctionComponent<Props> = ({ isShowSetupButton = false }) => {
  const [isShowAnimation, setIsShowAnimation] = useState(false);
  const toggleReference = useRef<HTMLDivElement>(null);
  const observer = useIntersectionObserver(toggleReference, { threshold: [1] });

  const alertList = useMemo(
    () =>
      Array.from({ length: 9 }, (_, index) => (
        <Alert $isActive={isShowAnimation} key={index}>
          <SvgAlert />
          <SvgAlertOn />
        </Alert>
      )),
    [isShowAnimation],
  );

  useEffect(() => {
    if (isShowAnimation && !observer?.isIntersecting) {
      setIsShowAnimation(false);
    } else {
      setIsShowAnimation(!!observer?.isIntersecting);
    }
  }, [isShowAnimation, observer?.isIntersecting]);

  const scrollToTop = () => {
    window.scrollTo({ behavior: "smooth", top: 0 });
  };

  return (
    <Container>
      <TextWrapper>
        <Title>Setting up alerts</Title>
        <Points>
          <Point>With daily alerts, you’ll always be up to date</Point>
          <Point>
            Receive{" "}
            <PointLink
              href={`${config.account.scmpAccountHost}/manage/notifications`}
              rel="noreferrer"
              target="_blank"
            >
              email alerts
            </PointLink>{" "}
            and reminders via push notification (using the{" "}
            <PointLink
              href="https://scmp.onelink.me/3586748601/b2l4v4j6"
              rel="noreferrer"
              target="_blank"
            >
              SCMP app
            </PointLink>
            )
          </Point>
          <Point>
            The news that matters to you, delivered directly to your device as it happens
          </Point>
        </Points>
      </TextWrapper>
      <AlertContainer>{alertList}</AlertContainer>
      <ImageContainer>
        <Toggle $isActive={isShowAnimation} ref={toggleReference}>
          <ToggleCircle $isActive={isShowAnimation} />
        </Toggle>
        <ImagePhone alt="Mynews" src={Phone.src} />
      </ImageContainer>
      <ButtonContainer>
        {isShowSetupButton ? (
          <Button onClick={scrollToTop}>Set up alerts</Button>
        ) : (
          <BaseLinkButton pathname="/mynews">Starting Reading</BaseLinkButton>
        )}
      </ButtonContainer>
    </Container>
  );
};

SettingUpAlert.displayName = "SettingUpAlert";
